export type Zone = 'left' | 'right';

export interface AnchorUpdate { zone: Zone; anchorY: number }

export interface TouchSnapshot {
  zone: Zone;
  centerX: number;
  centerY: number;
  lastY: number;
}

export interface DragTrackerOpts {
  viewportWidth: number;
  anchorMinY: number;
  anchorMaxY: number;
  initialLeftY: number;
  initialRightY: number;
  /** Multiplier applied to drag-layer dy before writing to the anchor. 1.0 = thumb-1:1. */
  dragScale: number;
  /** Hard cap on |leftAnchor − rightAnchor| in logical px. Pass POSITIVE_INFINITY to disable. */
  maxTiltDeltaPx: number;
}

export interface JoystickTuning {
  /** Half-width of the no-motion zone around the touch-down center, in logical px. */
  deadzonePx: number;
  /** Offset at which the speed curves saturate, in logical px. */
  refOffsetPx: number;
  /**
   * Max common-mode speed (vertical / camera pan), in logical px/sec. Driven by
   * the *average* of the two thumb offsets via a linear-saturating curve so the
   * camera pans at a steady speed regardless of how far past refOffsetPx the
   * thumbs are pulled.
   */
  maxVerticalSpeedPxPerSec: number;
  /**
   * Max differential-mode speed (tilt) per side, in logical px/sec. Driven by
   * each thumb's deviation from the common-mode average via the quadratic
   * curve, so light differential offsets give a gentle lean and large ones
   * snap the bar over quickly.
   */
  maxTiltSpeedPxPerSec: number;
}

interface DragPointer {
  id: number;
  zone: Zone;
  centerX: number;   // screen-X at touch-down (joystick horizontal anchor)
  centerY: number;   // screen-Y at touch-down (joystick center)
  lastY: number;     // screen-Y of the latest event (drag reference)
}

/**
 * Hybrid drag + joystick tracker. Two contributions to the bar anchor per
 * active pointer:
 *
 * - **Drag layer (instant, in `pointerMove`):** each event applies
 *   `(currentY - lastY)` directly to the anchor. The bar follows the thumb
 *   1:1 with no integration lag.
 *
 * - **Velocity layer (per-frame, in `tick`):** for each held pointer, the
 *   offset `(lastY - centerY)` is fed through a quadratic speed curve and
 *   integrated into the anchor over `dt`. While the thumb sits past the
 *   touch-down center, the bar keeps moving in that direction.
 *
 * Both layers compose through the same clamp and write path: fast drag at
 * large offset is drag + velocity; held thumb past center is velocity only;
 * thumb at center is neither.
 *
 * Lifting and re-touching captures a new `centerY` (and `lastY`), so neither
 * layer jumps on re-touch.
 */
export class DragTracker {
  private opts: DragTrackerOpts;
  private anchors: Record<Zone, number>;
  private zonePointers: Partial<Record<Zone, DragPointer>> = {};
  private idToZone = new Map<number, Zone>();

  constructor(opts: DragTrackerOpts) {
    this.opts = opts;
    this.anchors = { left: opts.initialLeftY, right: opts.initialRightY };
  }

  pointerDown(id: number, screenX: number, screenY: number): Zone | null {
    const zone: Zone = screenX < this.opts.viewportWidth / 2 ? 'left' : 'right';
    if (this.zonePointers[zone]) return null;
    this.zonePointers[zone] = { id, zone, centerX: screenX, centerY: screenY, lastY: screenY };
    this.idToZone.set(id, zone);
    return zone;
  }

  pointerMove(id: number, _screenX: number, screenY: number): AnchorUpdate | null {
    const zone = this.idToZone.get(id);
    if (zone === undefined) return null;
    const ptr = this.zonePointers[zone];
    if (!ptr) return null;
    const rawDy = screenY - ptr.lastY;
    ptr.lastY = screenY; // raw — velocity overlay uses raw offsets
    if (rawDy === 0) return null;
    const dy = rawDy * this.opts.dragScale;
    const next = this.clampWithTilt(zone, this.anchors[zone] + dy);
    if (next === this.anchors[zone]) return null;
    this.anchors[zone] = next;
    return { zone, anchorY: next };
  }

  pointerUp(id: number): void {
    const zone = this.idToZone.get(id);
    if (zone === undefined) return;
    this.idToZone.delete(id);
    delete this.zonePointers[zone];
  }

  /**
   * Per-frame velocity overlay. Each active pointer's deadzoned offset is
   * decomposed into a common-mode component (the average across both pointers,
   * driving vertical/camera motion) and a differential component (each side's
   * deviation from that average, driving tilt). The common-mode component
   * integrates with a *linear-saturating* curve so vertical pan speed is
   * constant past refOffsetPx — the camera doesn't accelerate when the user
   * pushes harder. The differential component keeps the quadratic curve so
   * tilt still ramps up aggressively under one-sided thumb pressure.
   *
   * An inactive pointer contributes offset=0 (held at center), so single-thumb
   * usage routes half of its offset to vertical and half to tilt — the bar
   * still leans and drifts on a single thumb, just with attenuated magnitude
   * compared to a two-thumb same-direction pull.
   */
  tick(dtMs: number, tuning: JoystickTuning): AnchorUpdate[] {
    const updates: AnchorUpdate[] = [];
    const dt = dtMs / 1000;

    const deadzoned = (zone: Zone): number => {
      const ptr = this.zonePointers[zone];
      if (!ptr) return 0;
      const raw = ptr.lastY - ptr.centerY;
      return Math.sign(raw) * Math.max(0, Math.abs(raw) - tuning.deadzonePx);
    };
    const oL = deadzoned('left');
    const oR = deadzoned('right');
    const common = (oL + oR) / 2;

    const vertNorm = Math.min(1, Math.abs(common) / tuning.refOffsetPx);
    const vertSpeed = Math.sign(common) * vertNorm * tuning.maxVerticalSpeedPxPerSec;

    for (const zone of ['left', 'right'] as const) {
      const ptr = this.zonePointers[zone];
      if (!ptr) continue;
      const oSelf = zone === 'left' ? oL : oR;
      const tilt = oSelf - common;
      const tiltNorm = Math.min(1, Math.abs(tilt) / tuning.refOffsetPx);
      const tiltSpeed = Math.sign(tilt) * tiltNorm * tiltNorm * tuning.maxTiltSpeedPxPerSec;
      const speed = vertSpeed + tiltSpeed;
      if (speed === 0) continue;
      const next = this.clampWithTilt(zone, this.anchors[zone] + speed * dt);
      if (next !== this.anchors[zone]) {
        this.anchors[zone] = next;
        updates.push({ zone, anchorY: next });
      }
    }
    return updates;
  }

  getAnchor(zone: Zone): number { return this.anchors[zone]; }

  getActiveTouchSnapshots(): TouchSnapshot[] {
    const out: TouchSnapshot[] = [];
    for (const zone of ['left', 'right'] as const) {
      const ptr = this.zonePointers[zone];
      if (!ptr) continue;
      out.push({ zone, centerX: ptr.centerX, centerY: ptr.centerY, lastY: ptr.lastY });
    }
    return out;
  }

  setAnchor(zone: Zone, y: number): void {
    this.anchors[zone] = this.clampWithTilt(zone, y);
  }

  /**
   * Reset both anchors atomically, bypassing the tilt clamp (which would otherwise
   * constrain each write to within maxTiltDeltaPx of the *other* anchor's stale
   * value). Used on level reset so the tracker returns to a known-flat state
   * regardless of where the previous level left it.
   */
  resetAnchors(left: number, right: number): void {
    const min = this.opts.anchorMinY;
    const max = this.opts.anchorMaxY;
    this.anchors.left  = Math.max(min, Math.min(max, left));
    this.anchors.right = Math.max(min, Math.min(max, right));
  }

  releaseAll(): void {
    this.zonePointers = {};
    this.idToZone.clear();
  }

  private clampWithTilt(zone: Zone, y: number): number {
    // 1) Per-anchor range.
    let v = Math.max(this.opts.anchorMinY, Math.min(this.opts.anchorMaxY, y));
    // 2) Tilt cap: |this anchor − other anchor| ≤ maxTiltDeltaPx.
    const other = zone === 'left' ? this.anchors.right : this.anchors.left;
    const max = this.opts.maxTiltDeltaPx;
    if (Number.isFinite(max)) {
      if (v > other + max) v = other + max;
      if (v < other - max) v = other - max;
    }
    return v;
  }
}
